/**
 * AUDIO — the whole mix, synthesized. There is not a single audio file in this
 * project: every engine, tyre, siren, radio station, raindrop and gunshot is
 * generated by the Web Audio API at runtime.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * PUBLIC API   const audio = ctx.get('audio')
 * ───────────────────────────────────────────────────────────────────────────
 *   audio.running                      graph is live (needs a user gesture)
 *   audio.start()                      force-start (returns Promise<boolean>)
 *   audio.resume()                     wake the mix from inside a user gesture:
 *                                      builds the graph on the first one and
 *                                      resumes a context the browser suspended
 *                                      later. This is what a UI calls in a
 *                                      click handler — see src/ui/boot.js.
 *   audio.deafness                     0..1 concussion; UI/post may read this
 *   audio.play(kind, position, opts)   one-shot; position null = head-locked
 *   audio.bark(kind, position, opts)   voice: 'spot' | 'reload' | 'grenade' |
 *                                      'flank' | 'suppress' | 'advance' |
 *                                      'hurt' | 'death' | 'copy'
 *   audio.dialogue(id, line, dur)      speak an authored story line in the
 *                                      speaker's own voice, duck the bed under
 *                                      it, and raise dialogue:start/:end. One
 *                                      at a time; barks yield to it.
 *   audio.ui(kind)                     'hitmarker'|'headshot'|'kill'|'damage'
 *   audio.radioNext()                  cycle station -> ... -> OFF -> station 1
 *   audio.setRadioStation(id|null)     'grease'|'gold'|'redline'|'slack'|
 *                                      'furnace'|'incline'
 *   audio.setRadioRotation(ids)        per-brother list, DESIGN.md BOYZ.*.radio
 *   audio.radioStation                 { id, name, tag } | null
 *   audio.setMasterVolume(v)  audio.setBusVolume(bus, v)
 *   audio.setAmbienceIntensity(v)      scales the city bed
 *   audio.report()                     diagnostics snapshot
 *
 * All of it is a no-op — never a throw — before the graph exists, so callers
 * never have to check whether audio started.
 *
 * SUBSYSTEMS
 *   engine.js    harmonic-order engine model, eight vehicle classes
 *   vehicles.js  engines, tyres, wind, suspension, skids, collisions, budget
 *   radio.js     six generative stations, idents, tuning static, cabin speaker
 *   city.js      district/hour/weather ambience bed, rain, thunder, wipers
 *   sirens.js    siren horns with doppler, dispatch chatter, pursuit layer
 *   spatial.js   HRTF emitter pool, distance, air absorption, occlusion, doppler
 *   mixer.js     buses, cabin filter, ducking, limiter, convolution reverb
 *
 * Driven off the canonical events in ARCHITECTURE.md: weapon:fire,
 * weapon:reload, weapon:shell, bullet:impact, bullet:tracer, damage:dealt,
 * damage:taken, actor:death, player:land, player:footstep, player:state,
 * explosion, vehicle:engine, vehicle:skid, vehicle:collision, vehicle:enter,
 * vehicle:exit, vehicle:destroyed, wanted:change, weather:change, time:hour —
 * plus the gameplay-jingle set (see jingles.js): ui:station retunes the radio
 * (queued until the graph exists, so a save restored before the first user
 * gesture still lands), economy:cash / money:change ring the register,
 * pickup:collect chimes per kind, game:service ticks while a repair/refuel
 * runs, police:busted and player:wasted get their four-note stings,
 * mission:start gets its arpeggio, and player:state.nitroOn drives the nitro
 * ignition + sustain layer.
 *
 * STORY DIALOGUE is the one thing here that is POLLED rather than driven by an
 * event, and the reason is worth writing down. Twenty-four chapters of authored
 * dialogue are performed by `ui`'s cutscene player and its subtitle bar, and
 * neither raises an event when a line goes up — there is no `dialogue:*` in
 * ARCHITECTURE.md's table because until now nothing needed one. So `_pollDialogue`
 * reads the live line off `ui` at runtime (`ctx.peek('ui')`, never an import —
 * rule 2, and the same shape as `_pollSky`), voices it, and RAISES the missing
 * events itself: `dialogue:start { id, text, dur, syllables, source }` and
 * `dialogue:end { id, reason }`, each a FRESH object a listener may keep. If
 * `ui`/`game` ever emit those at the source, this poller should be deleted in
 * the same change — the emit is the contract, the poll is the scaffolding
 * under it.
 */

import {
  NoiseBank, SPEED_OF_SOUND, biquad, clamp, gain as mkGain, osc, saturationCurve,
  series, shaper,
} from './dsp.js';
import { Mixer } from './mixer.js';
import { SpatialField } from './spatial.js';
import { ambientOneShot, ONE_SHOTS } from './ambience.js';
import { CityAmbience, CITY_EVENTS, cityOneShot } from './city.js';
import { VehicleAudio } from './vehicles.js';
import { Radio, STATIONS, STATION_IDS } from './radio.js';
import { PoliceAudio } from './sirens.js';
import { WEAPON_PROFILES, resolveProfile, weaponShot, bulletWhizz, dryFire } from './weapons.js';
import {
  surfaceImpact, footstep, shellCasing, reloadPhase, explosion, bodyFall, uiSound,
  heartbeat, cloth,
} from './foley.js';
import { jingle, hasJingle } from './jingles.js';
import { bark as voxBark, barkFor, DialogueVoice } from './vox.js';
import { classifySpace } from './ir.js';

const PROBE_RAYS = 9;
const PROBE_DIST = 40;
const DRY_SLOTS = 48;
const GESTURES = ['pointerdown', 'mousedown', 'keydown', 'touchstart', 'wheel'];

/** Names other subsystems already use, mapped onto our voices. */
const UI_ALIAS = {
  hit_flesh: 'hitmarker', hit: 'hitmarker', hit_head: 'headshot', headshot: 'headshot',
  hit_kill: 'kill', hit_armour: 'armour', player_hurt: 'damage', hurt: 'damage',
  weapon_fire_dry: 'dryfire', dry: 'dryfire', low_health: 'lowhealth',
  dead: 'wasted', mission_passed: 'mission_pass', mission_failed: 'mission_fail',
};

/** Default bus per voice kind, so callers do not have to know the mix layout. */
const BUS_FOR = {
  shot: 'weapons', explosion: 'weapons', dryfire: 'weapons',
  hitmarker: 'ui', headshot: 'ui', kill: 'ui', armour: 'ui', damage: 'ui',
  grenade_warn: 'ui', regen: 'ui', lowhealth: 'ui',
  bark: 'voice', ambient: 'ambience', city: 'ambience',
  // The nitro ignition is engine-side hardware, not HUD feedback; every other
  // jingle default-routes to 'ui' via ui()/play().
  nitro: 'vehicles',
};

/** pickup:collect kind -> jingle id. Unlisted kinds get the generic chime. */
const PICKUP_JINGLE = {
  cash: 'pickup_cash', money: 'pickup_cash',
  health: 'pickup_health', armor: 'pickup_armor', armour: 'pickup_armor',
  ammo: 'pickup_ammo', nitro: 'pickup_nitro',
};

/** Finite Vector3-ish check — one NaN from any subsystem must not throw. */
function isVec(p) {
  return !!p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
}

export class AudioSystem {
  static id = 'audio';
  static deps = [];

  constructor() {
    this.running = false;
    this.failed = false;
    this.actx = null;
    this.mixer = null;
    this.field = null;
    this.ambience = null;
    this.vehicles = null;
    this.radio = null;
    this.police = null;
    this.bank = null;
    this.deafness = 0;

    /* ---- open-world state ------------------------------------------ */
    this.inVehicle = false;
    this._cabinTarget = 0;
    this._cabin = 0;
    this._ownVehicle = null;
    this._ownPos = { x: 0, y: 0, z: 0, has: false };
    this._weather = { rain: 0, wind: 0.3, wetness: 0, state: 'clear' };
    this._hour = 12;
    this._wanted = 0;
    this._skyPoll = 0;
    this._pendingRotation = null;
    /**
     * The station asked for before the graph exists. The first user gesture is
     * what builds the AudioContext, but `ui:station` can arrive earlier — the
     * save system re-emits it on load, and the boot character switch tunes his
     * rotation — so the latest request is queued here (null = OFF is a real
     * request; undefined = nobody has asked) and applied in start().
     */
    this._pendingStation = undefined;

    /* ---- story dialogue -------------------------------------------- */
    /** Built with the graph; null until then. See `_pollDialogue`. */
    this.vox = null;
    /**
     * Which line is already spoken. IDENTITY, not content: the cutscene's own
     * `lines` array reference plus its index, and for the subtitle bar the
     * direction of its `t`. Held across a pause on purpose — clearing it is how
     * a resumed scene speaks the same line twice.
     */
    this._cutLines = null;
    this._cutIdx = -1;
    this._barkT = -1;
    this._dlgHeld = false;
    /**
     * NEVER HAND A REUSED RECORD TO THE BUS.
     *
     * `DialogueVoice` allocates nothing per line: `speak` and `stop` fill one
     * `_startRec` / `_endRec` in place and hand it to these callbacks, which is
     * the right contract for a callback — the callee reads it and returns. It
     * is the wrong one for `ctx.events`, which is a broadcast to code neither
     * file knows about, and a listener has every reason to stash what it is
     * given: a mission log, an achievement counter, a subtitle-timing widget.
     * The next line rewrites the object under all of them at once, and nothing
     * in the tree would catch it — the only consumer today is `duckprobe.mjs`,
     * which copies on receipt.
     *
     * A copy at the boundary is the whole fix and it is free: one small object
     * per spoken line, and the budget is one voice at a time.
     */
    this._onDlgStart = (r) => {
      this.stats.dialogue++;
      this.ctx.events.emit('dialogue:start', {
        id: r.id, text: r.text, dur: r.dur, syllables: r.syllables, source: r.source,
      });
    };
    this._onDlgEnd = (r) => this.ctx.events.emit('dialogue:end', { id: r.id, reason: r.reason });
    /** `actx.currentTime` at the last frame — the mixer's clock. See update(). */
    this._audioT = -1;

    /* ---- gameplay jingles ------------------------------------------ */
    this._service = { active: false, kind: 'repair', timer: 0, until: 0 };
    this._nitro = { on: false, evOn: false, pollOn: false };
    this._nitroLoop = null;
    this._lastCashT = -9;
    this._cashEventSeen = false;

    /* preallocated scratch — update() allocates nothing */
    this._probeDirs = [];
    for (let i = 0; i < PROBE_RAYS - 1; i++) {
      const a = (i / (PROBE_RAYS - 1)) * Math.PI * 2;
      this._probeDirs.push({ x: Math.cos(a), y: 0.06, z: Math.sin(a) });
    }
    this._probeDirs.push({ x: 0, y: 1, z: 0 });
    this._probeHits = new Float64Array(PROBE_RAYS);
    this._space = {
      tight: 0, room: 0, street: 0.35, tunnel: 0, open: 0.65,
      enclosure: 0, meanFree: PROBE_DIST, ceiling: PROBE_DIST,
    };
    this._probeTimer = 0;
    this._lastProbe = { x: 1e9, y: 0, z: 0 };
    this._origin = { x: 0, y: 0, z: 0 };

    /* dry (head-locked) voice bookkeeping */
    this._dry = [];
    for (let i = 0; i < DRY_SLOTS; i++) this._dry.push({ node: null, send: null, end: 0 });
    this._dryCursor = 0;

    /* per-frame rate limits */
    this._budget = { impact: 0, step: 0, shell: 0, whizz: 0 };
    this._lastBarkTime = -99;
    this._lastEnemyFire = -99;

    this._health = 100;
    this._heartTimer = 0;
    this._stance = null;
    this._ads = false;

    this.stats = {
      voices: 0, dropped: 0, stolen: 0, rays: 0, deafness: 0,
      space: 'open', started: false, contextState: 'none', events: 0, errors: 0, ms: 0,
      dialogue: 0, barksYielded: 0,
    };

    this._offs = [];
    this._gestureHandler = null;
    this._ambienceApi = null;
    /** In-flight start(), so two gestures cannot build two graphs. */
    this._starting = null;
  }

  /* ================================================================ */
  /* lifecycle                                                        */
  /* ================================================================ */

  async init(ctx) {
    this.ctx = ctx;
    this.rng = ctx.rng.fork();
    this._wireEvents(ctx);

    /*
     * Web Audio needs a user gesture. Arm every plausible one; the first to
     * land builds the graph. Capture mode never gestures, so shots render in
     * silence and stay byte-identical.
     *
     * ── WHY CAPTURE PHASE ──────────────────────────────────────────────────
     * These used to be bubble-phase listeners, and that shipped a game which
     * booted PERMANENTLY SILENT on any mouse-only desktop. The boot overlay
     * (`src/ui/boot.js`) is position:fixed inset:0 over the whole viewport for
     * the entire boot flow, and it calls `stopPropagation` on mousedown /
     * mouseup / pointerdown / pointerup — it has to, because `Input._onMouseDown`
     * is on window and grabs pointer lock on any left click, which would eat
     * START and every character card. So the player clicked his way into the
     * city and not one pointer event ever reached window: `running` stayed
     * false and every voice was dropped, until he happened to press a key.
     * keydown and touchstart were not on boot's stopPropagation list, which is
     * exactly why every keyboard-driven probe in this repo missed it.
     *
     * Capture runs on the way DOWN, from window to the target, so it fires
     * before any element in the tree gets the chance to stop it. Unlocking the
     * mix must not depend on every future overlay remembering not to swallow a
     * pointer event.
     */
    const kick = () => {
      this.resume()
        .then((live) => { if (live || this.failed) this._disarmGesture(); })
        .catch(() => {});
    };
    this._gestureHandler = kick;
    if (typeof addEventListener === 'function') {
      for (const ev of GESTURES) {
        addEventListener(ev, kick, { passive: true, capture: true });
      }
    }
    if (typeof window !== 'undefined') window.__AUDIO__ = this;
  }

  _disarmGesture() {
    if (!this._gestureHandler) return;
    // The capture flag is part of a listener's identity: remove it without
    // `capture: true` and the listener stays armed forever.
    for (const ev of GESTURES) {
      removeEventListener(ev, this._gestureHandler, { capture: true });
    }
    this._gestureHandler = null;
  }

  /**
   * Wake the mix on a user gesture — the one call a UI should make from inside
   * a click. Builds the graph on the first gesture and resumes a context the
   * browser suspended later (backgrounded tab, OS audio focus change), which
   * `start()` deliberately will not do: it early-returns on `running`.
   *
   * Resolves to whether the context is actually rendering afterwards, so the
   * caller can sequence a sound behind it — the click that legalises Web Audio
   * is usually the same click that wants to play the first sound, and the graph
   * does not exist yet at that instant.
   */
  async resume() {
    if (!this.running) return this.start();
    const actx = this.actx;
    if (!actx) return false;
    if (actx.state === 'suspended') {
      try { await actx.resume(); } catch { return false; }
    }
    this.stats.contextState = actx.state;
    return actx.state === 'running';
  }

  /**
   * Build the graph. Safe to call repeatedly; resolves false when audio is
   * unavailable (no AudioContext, blocked autoplay, headless renderer, ...).
   *
   * CONCURRENCY. One click delivers `pointerdown` AND `mousedown` as separate
   * tasks, so `start()` is genuinely called twice before the first has finished.
   * `this.running` is not set until the very end, and the body awaits
   * `actx.resume()` in the middle — so without this guard the second call sails
   * past the `running` check and builds a SECOND AudioContext and a second
   * complete graph, both wired to their own destination. The orphan is
   * unreachable, un-disposable and audible forever: the whole city, twice.
   */
  start() {
    if (this.running) return Promise.resolve(true);
    if (this.failed) return Promise.resolve(false);
    if (this._starting) return this._starting;
    this._starting = this._startOnce().finally(() => { this._starting = null; });
    return this._starting;
  }

  async _startOnce() {
    try {
      const AC = globalThis.AudioContext ?? globalThis.webkitAudioContext;
      if (!AC) throw new Error('no AudioContext');
      const actx = new AC({ latencyHint: 'interactive' });
      this.actx = actx;

      this.bank = new NoiseBank(actx, this.rng.fork(), 2.4);
      this.mixer = new Mixer(actx, this.rng.fork(), {});
      this.mixer.buildReverbs();
      this.field = new SpatialField(actx, this.mixer, this.ctx);
      this.ambience = new CityAmbience(actx, this.bank, this.mixer, this.field, this.rng.fork(), this.ctx);
      this.ambience.start();
      this.ambience.setHour(this._hour);
      this.ambience.setWeather(this._weather);
      this.vehicles = new VehicleAudio(actx, this.bank, this.mixer, this.field, this.rng.fork(), this.ctx);
      this.vehicles.setWeather(this._weather.wetness);
      this.radio = new Radio(actx, this.bank, this.mixer, this.field, this.rng.fork());
      if (this._pendingRotation) this.radio.setRotation(this._pendingRotation);
      this.radio.setCabin(0, 0.001);
      // Whatever the HUD/save asked for while there was no graph to hear it.
      if (this._pendingStation !== undefined) this.radio.tune(this._pendingStation);
      this.police = new PoliceAudio(actx, this.bank, this.mixer, this.field, this.rng.fork());
      this.police.setWanted(this._wanted, 0);
      // Its own fork: `speak` reseeds it per line so a replayed chapter sounds
      // the same, and nothing else may be sharing that stream.
      this.vox = new DialogueVoice(actx, this.bank, this.mixer, this.rng.fork());
      this.vox.onStart = this._onDlgStart;
      this.vox.onEnd = this._onDlgEnd;
      this.mixer.setSpace(this._space, 0.001);

      if (actx.state === 'suspended') await actx.resume();
      this.running = true;
      this.stats.started = true;
      this.stats.contextState = actx.state;
      console.info(`[audio] online @ ${actx.sampleRate} Hz`);
      return true;
    } catch (err) {
      console.warn('[audio] disabled:', err?.message ?? err);
      this.failed = true;
      this._teardown();
      return false;
    }
  }

  _teardown() {
    if (this._nitroLoop) {
      for (const n of this._nitroLoop.nodes) {
        try { n.stop?.(); } catch { /* not a source */ }
        try { n.disconnect(); } catch { /* already gone */ }
      }
      this._nitroLoop = null;
    }
    this._nitro.on = false;
    this._service.active = false;
    this._cutLines = null;
    this._cutIdx = -1;
    this._barkT = -1;
    this._audioT = -1;
    try {
      this.vox?.dispose();
      this.police?.dispose();
      this.radio?.dispose();
      this.vehicles?.dispose();
      this.ambience?.dispose();
      this.field?.dispose();
      this.mixer?.dispose();
      this.bank?.dispose();
      if (this.actx && this.actx.state !== 'closed') this.actx.close();
    } catch { /* nothing useful to do */ }
    this.police = this.radio = this.vehicles = this.vox = null;
    this.ambience = this.field = this.mixer = this.bank = null;
    this.actx = null;
    this.running = false;
  }

  dispose() {
    this._disarmGesture();
    for (const off of this._offs) off();
    this._offs.length = 0;
    this._teardown();
    if (typeof window !== 'undefined' && window.__AUDIO__ === this) delete window.__AUDIO__;
  }

  /* ================================================================ */
  /* frame                                                            */
  /* ================================================================ */

  update(dt, ctx) {
    if (!this.running) return;
    const t0 = performance.now();
    try {
      const actx = this.actx;
      if (actx.state === 'suspended') return; // tab hidden, or resume pending

      /* ---- listener from the render camera ----------------------- */
      const cam = ctx.camera;
      cam.updateMatrixWorld();
      const e = cam.matrixWorld.elements;
      this.field.setListener(
        e[12], e[13], e[14],
        -e[8], -e[9], -e[10],
        e[4], e[5], e[6],
        dt
      );

      /* ---- space probe ------------------------------------------- */
      this._probeTimer -= dt;
      const moved = Math.abs(e[12] - this._lastProbe.x) + Math.abs(e[13] - this._lastProbe.y) +
        Math.abs(e[14] - this._lastProbe.z);
      if (this._probeTimer <= 0 || moved > 1.6) {
        this._probeTimer = 0.45;
        this._lastProbe.x = e[12]; this._lastProbe.y = e[13]; this._lastProbe.z = e[14];
        this._probeSpace(ctx, e[12], e[13], e[14]);
      }

      /* ---- subsystems -------------------------------------------- */
      /*
       * THE MIXER RUNS ON THE AUDIO CLOCK, NOT THE FRAME CLOCK, and it is the
       * only thing here that does. Everything `Mixer.update` drives — duck
       * recovery, the hearing-damage tail — is automation it has ALREADY
       * scheduled on `actx.currentTime`, which does not stop when `time.scale`
       * does. Fed the scaled dt it froze all three whenever the world stopped:
       * a cutscene holds `time.scale` at 0 for as long as the scene runs, so a
       * gunshot duck taken one frame before the cut stayed down for the whole
       * chapter intro, and a story line's own -9 dB duck (below) would have had
       * no clock at all to release on. `dt` is still what every other subsystem
       * here gets — they model the WORLD, and the world really is stopped.
       */
      const audioNow = actx.currentTime;
      const aDt = this._audioT < 0 ? dt : clamp(audioNow - this._audioT, 0, 0.25);
      this._audioT = audioNow;
      this.mixer.update(aDt);
      /* Story dialogue: after the mixer, because its side-chain re-assert has to
         land after that frame's decay. See DialogueVoice's header. */
      this._pollDialogue();
      this.vox.update(audioNow);
      this.field.update(dt);
      this.deafness = this.mixer.deafness;
      if (!this._ambienceApi) {
        this._ambienceApi = {
          cityEvent: (layer) => this._cityEvent(layer),
          thunder: () => this._thunder(),
        };
      }
      this._updateCabin(dt);
      this.vehicles.update(dt);
      this.police.update(dt, this.vehicles._order);
      this.radio.update(dt);
      this.ambience.update(dt, this._ambienceApi);
      this._pollSky(dt);

      /* ---- head-locked voice teardown ---------------------------- */
      const now = audioNow;
      for (let i = 0; i < this._dry.length; i++) {
        const d = this._dry[i];
        if (!d.node || now < d.end) continue;
        try { d.node.disconnect(); } catch { /* already gone */ }
        try { d.send?.disconnect(); } catch { /* already gone */ }
        d.node = null; d.send = null;
      }

      /* ---- gameplay layers --------------------------------------- */
      this._updateService(dt);
      this._applyNitro();

      /* ---- low-health heartbeat ---------------------------------- */
      if (this._health < 34) {
        this._heartTimer -= dt;
        if (this._heartTimer <= 0) {
          this._heartTimer = 0.62 + (this._health / 34) * 0.45;
          this._playDry('heartbeat', { level: clamp(1 - this._health / 34, 0.2, 1) }, 'foley', 0.1);
        }
      }

      /* ---- reset per-frame budgets ------------------------------- */
      const b = this._budget;
      b.impact = 0; b.step = 0; b.shell = 0; b.whizz = 0;

      const s = this.stats;
      s.voices = this.field.stats.active;
      s.dropped = this.field.stats.dropped;
      s.stolen = this.field.stats.stolen;
      s.rays = this.field.stats.occlusionRays;
      s.deafness = this.deafness;
      s.contextState = actx.state;
    } catch (err) {
      this._error(err);
    }
    // Main-thread cost only — the synthesis itself runs on the audio thread.
    // What is measured here is the vehicle ranking, the radio scheduler, the
    // space probe and the parameter writes.
    this.stats.ms = this.stats.ms * 0.92 + (performance.now() - t0) * 0.08;
  }

  _error(err) {
    this.stats.errors++;
    if (this.stats.errors < 6) console.error('[audio]', err);
    if (this.stats.errors === 40) {
      console.error('[audio] too many errors — disabling audio');
      this.failed = true;
      this._teardown();
    }
  }

  /* ================================================================ */
  /* environment probe                                                */
  /* ================================================================ */

  _probeSpace(ctx, x, y, z) {
    const phys = ctx.peek('physics');
    const hits = this._probeHits;
    if (phys?.raycast) {
      const mask = phys.MASK?.WORLD;
      const o = this._origin;
      o.x = x; o.y = y; o.z = z;
      for (let i = 0; i < PROBE_RAYS; i++) {
        const h = phys.raycast(o, this._probeDirs[i], PROBE_DIST, mask);
        hits[i] = h?.hit ? h.distance : PROBE_DIST;
      }
    } else {
      for (let i = 0; i < PROBE_RAYS; i++) hits[i] = PROBE_DIST;
    }
    classifySpace(hits, PROBE_DIST, this._space);
    this.mixer.setSpace(this._space, 0.4);
    this.ambience.setEnclosure(this._space.enclosure);

    let best = 'open', bv = -1;
    for (const k of ['tight', 'room', 'street', 'tunnel', 'open']) {
      if (this._space[k] > bv) { bv = this._space[k]; best = k; }
    }
    this.stats.space = best;
  }

  /* ================================================================ */
  /* open world state                                                 */
  /* ================================================================ */

  /**
   * The single biggest EQ move in the game: getting into a car. The street
   * closes down, the radio comes up out of the door cards, the engine arrives
   * through the firewall instead of through the air. Ramped, not switched.
   */
  _updateCabin(dt) {
    const target = this.inVehicle ? 1 : 0;
    this._cabin += (target - this._cabin) * clamp(dt * 4.5, 0, 1);
    if (Math.abs(this._cabin - target) < 0.004) this._cabin = target;
    // Push only when it has actually moved: each push is four setTargetAtTime
    // ramps across four subsystems and there is no point doing that at 60 Hz.
    if (Math.abs(this._cabin - this._cabinTarget) > 0.02 ||
        (this._cabin === target && this._cabinTarget !== target)) {
      this._cabinTarget = this._cabin;
      this.mixer.setCabin(this._cabin, 0.12);
      this.vehicles.setCabin(this._cabin);
      this.radio.setCabin(this._cabin, 0.12);
      this.ambience.setCabin(this._cabin);
    }

    /* Track the car we last got out of: the radio is still playing in it. */
    if (this._ownVehicle) {
      const r = this.vehicles.records.get(this._ownVehicle);
      if (r) {
        this._ownPos.x = r.x; this._ownPos.y = r.y; this._ownPos.z = r.z;
        this._ownPos.has = true;
      }
    }

    /* Outside the car the radio is a source in the world, heard through a
       closed door: bass through the panels, no top end, and it dopplers if the
       car is rolling away from you. */
    if (this.inVehicle || !this.radio.current) {
      this.radio.clearSource();
    } else if (this._ownPos.has) {
      const lp = this.field.listenerPos;
      const d = Math.hypot(this._ownPos.x - lp.x, this._ownPos.y - lp.y, this._ownPos.z - lp.z);
      if (d < 60) this.radio.setSource(this._ownPos.x, this._ownPos.y + 0.6, this._ownPos.z, dt);
      else this.radio.clearSource();
    }
  }

  /**
   * `sky` is the authority on time and weather, but it may emit `sky:changed`
   * rather than the canonical events while it is being written. Poll it slowly
   * as a belt and braces so the ambience is never stuck at noon in the dry.
   */
  _pollSky(dt) {
    this._skyPoll -= dt;
    if (this._skyPoll > 0) return;
    this._skyPoll = 2;
    const sky = this.ctx.peek?.('sky');
    if (!sky) return;
    const h = sky.timeOfDay ?? sky.hour;
    if (Number.isFinite(h)) {
      this._hour = h;
      this.ambience.setHour(h);
    }
    const w = sky.weather;
    if (w) {
      const rain = w.rain ?? w.rainAmount ?? w.precipitation ?? 0;
      const wet = w.wetness ?? rain;
      const wind = w.wind ?? w.windSpeed ?? this._weather.wind;
      if (Number.isFinite(rain)) this._weather.rain = clamp(rain, 0, 1);
      if (Number.isFinite(wet)) this._weather.wetness = clamp(wet, 0, 1);
      if (Number.isFinite(wind)) this._weather.wind = clamp(wind, 0, 1);
      this.ambience.setWeather(this._weather);
      this.vehicles.setWeather(this._weather.wetness);
    }
  }

  /** Drop a positioned city event from the layer the scheduler chose. */
  _cityEvent(layer) {
    if (!this.running) return;
    const rng = this.rng;
    const table = CITY_EVENTS[layer] ?? CITY_EVENTS.traffic;
    const which = rng.pick(table);
    const lp = this.field.listenerPos;
    const far = which === 'pass' || which === 'boathorn' || which === 'clank' || which === 'steam';
    const d = far ? rng.range(35, 190) : rng.range(9, 70);
    const a = rng.range(0, Math.PI * 2);
    const y = which === 'gull' || which === 'crow' ? rng.range(6, 22)
      : which === 'fanstart' || which === 'hvac' ? rng.range(4, 14)
        : rng.range(-1.2, 2.5);
    this._playAt('city',
      lp.x + Math.cos(a) * d, lp.y + y, lp.z + Math.sin(a) * d,
      {
        which, level: rng.range(0.55, 1), maxDist: 320,
        // Rooftop and distant things arrive over the buildings, not through
        // them; occluding a gull through a wall it is flying over is wrong.
        occlusion: far || y > 5 ? 0 : undefined,
        gain: far ? 3.6 : 1.6,
      }, 'ambience', 0.18);
  }

  /** A strike somewhere over the city, with the right delay for its distance. */
  _thunder() {
    if (!this.running) return;
    const rng = this.rng;
    const lp = this.field.listenerPos;
    const near = Math.pow(rng.float(), 1.6);
    const dist = (1 - near) * 2600 + 220;
    const a = rng.range(0, Math.PI * 2);
    // Distance is what makes thunder thunder: the delay, the loss of the crack,
    // and the roll. Only the level is scaled here; the shape comes from `near`.
    this._playAt('city',
      lp.x + Math.cos(a) * Math.min(dist, 260),
      lp.y + rng.range(40, 160),
      lp.z + Math.sin(a) * Math.min(dist, 260),
      {
        which: 'thunder', near, level: 1, maxDist: 4000, occlusion: 0,
        gain: 6 + near * 10, noDelay: true, extraDelay: dist / SPEED_OF_SOUND,
      }, 'ambience', 0.4);
  }

  /* ================================================================ */
  /* radio                                                            */
  /* ================================================================ */

  /** Cycle to the next station, then OFF, then back round. */
  radioNext() {
    return this.radio?.next() ?? null;
  }

  setRadioStation(id) {
    this._pendingStation = id ?? null;
    this.radio?.tune(id ?? null);
  }

  /**
   * `ui:station` — THE retune path. The HUD cycles stations (R / the phone)
   * and the save system re-emits the stored station on load; both arrive
   * here. OFF is a first-class stop on the dial: the HUD publishes it as
   * id -1 (it has no station id for it), and null / 'off' mean the same.
   * Unknown ids are coerced to OFF by Radio.tune rather than ignored, so a
   * save from a build with a station this one lacks degrades to silence, not
   * to a stuck handler.
   */
  _onStation(p) {
    let id = p?.id;
    if (id == null || id === -1 || id === 'off') id = null;
    else if (typeof id === 'number') id = STATIONS[STATION_IDS[id]] ? STATION_IDS[id] : null;
    else id = String(id);
    this.setRadioStation(id);
  }

  /** DESIGN.md gives each brother his own rotation (`BOYZ.*.radio`). */
  setRadioRotation(ids) {
    this._pendingRotation = ids;
    this.radio?.setRotation(ids);
  }

  get radioStation() {
    const id = this.radio?.current;
    if (!id) return null;
    const s = STATIONS[id];
    return { id, name: s.name, tag: s.tag, bpm: s.bpm };
  }

  /* ================================================================ */
  /* voice plumbing                                                   */
  /* ================================================================ */

  /**
   * Build a voice by name. `when` is absolute context time, `dist` is metres
   * from the listener — voices use it to rebalance their own layers.
   */
  _build(kind, when, dist, o) {
    const { actx, bank } = this;
    const rng = this.rng;
    switch (kind) {
      case 'shot':
        return weaponShot(actx, bank, rng, o.profile ?? WEAPON_PROFILES.rifle, {
          when, distance: dist, firstPerson: o.firstPerson,
          echoBoost: 0.75 + this._space.street * 0.7 + this._space.tight * 0.35 +
            this._space.tunnel * 0.8 + this._space.open * 0.2,
        });
      case 'whizz': return bulletWhizz(actx, bank, rng, { when, miss: o.miss, gain: o.gain });
      case 'dryfire': return dryFire(actx, bank, rng, { when });
      case 'impact': return surfaceImpact(actx, bank, rng, { when, surface: o.surface, energy: o.energy });
      case 'step': return footstep(actx, bank, rng, { when, surface: o.surface, gait: o.gait, level: o.level, gear: o.gear });
      case 'shell': return shellCasing(actx, bank, rng, { when, surface: o.surface, level: o.level, flight: o.flight });
      case 'reload': return reloadPhase(actx, bank, rng, o.phase, { when, heavy: o.heavy });
      case 'explosion': return explosion(actx, bank, rng, { when, distance: dist, radius: o.radius, level: o.level });
      case 'bodyfall': return bodyFall(actx, bank, rng, { when, level: o.level });
      case 'cloth': return cloth(actx, bank, rng, { when, level: o.level });
      case 'heartbeat': return heartbeat(actx, bank, rng, { when, level: o.level });
      case 'bark': return voxBark(actx, bank, rng, { when, bark: o.bark, f0: o.f0, tract: o.tract, level: o.level, radio: o.radio });
      case 'ambient': return ambientOneShot(actx, bank, rng, o.which, { when, level: o.level });
      case 'city': return cityOneShot(actx, bank, rng, o.which, { when, level: o.level, near: o.near });
      default:
        // Gameplay jingles (cash, pickups, mission cards, stings, clicks) get
        // their authored voices; anything truly unknown still lands on the
        // uiSound generic blip so a new caller is audible before it is styled.
        if (hasJingle(kind)) return jingle(actx, bank, rng, kind, { when, level: o.level, spend: o.spend });
        return uiSound(actx, bank, rng, kind, { when, level: o.level });
    }
  }

  /**
   * Spatialised one-shot: propagation delay, occlusion, air absorption and the
   * reverb send. Returns false when the voice budget refused it.
   */
  _playAt(kind, x, y, z, o = {}, bus = 'foley', priority = 0.5) {
    if (!this.running || this.actx.state === 'suspended') return false;
    if (!Number.isFinite(x + y + z)) return this._playDry(kind, o, bus, o.send ?? 0.15);
    try {
      const field = this.field;
      const dist = field.distanceTo(x, y, z);
      if (dist > (o.maxDist ?? 320)) return false;
      const delay = o.noDelay ? 0 : dist / SPEED_OF_SOUND;
      const when = this.actx.currentTime + delay + (o.extraDelay ?? 0);
      const voice = this._build(kind, when, dist, o);
      const em = field.acquire({
        x, y, z, when, dist, bus, priority,
        send: o.send ?? voice.send ?? 0.3,
        gain: o.gain ?? 1,
        endTime: voice.end,
        occlusion: o.occlusion,
        tracked: o.tracked,
      });
      if (!em) {
        try { voice.node.disconnect(); } catch { /* noop */ }
        return false;
      }
      field.hold(em, voice.node, voice.end);
      this.stats.events++;
      return true;
    } catch (err) {
      this._error(err);
      return false;
    }
  }

  /** Head-locked one-shot: own weapon, UI, player grunts, heartbeat. */
  _playDry(kind, o = {}, bus = 'foley', send = 0.15) {
    if (!this.running || this.actx.state === 'suspended') return false;
    try {
      const when = this.actx.currentTime + (o.extraDelay ?? 0);
      const voice = this._build(kind, when, o.dist ?? 0, o);
      const g = mkGain(this.actx, o.gain ?? 1);
      voice.node.connect(g);
      g.connect(this.mixer.bus(bus));
      let sendNode = null;
      const sendLevel = (o.send ?? send) * (voice.send ?? 1);
      if (sendLevel > 0.001) {
        sendNode = mkGain(this.actx, sendLevel);
        g.connect(sendNode);
        sendNode.connect(this.mixer.busSend(bus));
      }
      // Claim a bookkeeping slot; steal the oldest if all are busy.
      let slot = null;
      for (let i = 0; i < this._dry.length; i++) {
        const idx = (this._dryCursor + i) % this._dry.length;
        if (!this._dry[idx].node) {
          slot = this._dry[idx];
          this._dryCursor = (idx + 1) % this._dry.length;
          break;
        }
      }
      if (!slot) {
        slot = this._dry[this._dryCursor];
        try { slot.node.disconnect(); slot.send?.disconnect(); } catch { /* noop */ }
        this._dryCursor = (this._dryCursor + 1) % this._dry.length;
      }
      slot.node = g;
      slot.send = sendNode;
      slot.end = voice.end + 0.05;
      this.stats.events++;
      return true;
    } catch (err) {
      this._error(err);
      return false;
    }
  }

  /* ================================================================ */
  /* public helpers                                                   */
  /* ================================================================ */

  /**
   * Fire a one-shot. Tolerant on purpose: other subsystems call this with
   * several different conventions and audio must never be the thing that throws.
   *   play('impact', vec3, { surface })   play('hit_flesh', { gain: 0.7 })
   *   play('shell', vec3, 0.8)
   */
  play(kind, position, opts) {
    if (typeof opts === 'number') opts = { gain: opts };
    if (typeof position === 'number') { opts = { gain: position }; position = null; }
    // A caller may have passed an options bag where a position goes.
    if (position && !isVec(position)) {
      opts = opts ?? position;
      position = null;
    }
    opts = opts ?? {};
    const k = UI_ALIAS[kind] ?? kind;
    if (position) {
      return this._playAt(k, position.x, position.y, position.z, opts,
        opts.bus ?? BUS_FOR[k] ?? 'foley', opts.priority ?? 0.5);
    }
    return this._playDry(k, opts, opts.bus ?? BUS_FOR[k] ?? 'foley', opts.send ?? 0.15);
  }

  ui(kind, level = 1) {
    const k = UI_ALIAS[kind] ?? kind;
    return this._playDry(k, { level }, BUS_FOR[k] ?? 'ui', k === 'heartbeat' ? 0.1 : 0);
  }

  /** Adapter the `ui` subsystem probes for: playUi(id, gain). */
  playUi(id, gain = 1) {
    return this.ui(id, gain);
  }

  /** Adapter the `fx` subsystem probes for: playShell(position, gain). */
  playShell(position, gain = 1) {
    if (!isVec(position)) return false;
    return this._playAt('shell', position.x, position.y, position.z,
      { level: gain, surface: 'concrete', flight: 0.02 }, 'foley', 0.25);
  }

  /** Adapter for impact FX that would rather call directly than emit. */
  playImpact(position, surface = 'concrete', energy = 1) {
    if (!isVec(position)) return false;
    return this._playAt('impact', position.x, position.y, position.z, { surface, energy }, 'foley', 0.55);
  }

  /** Enemy vocalisation. `kind` is semantic — see barkFor() in vox.js. */
  bark(kind, position, opts = {}) {
    if (!this.running) return false;
    const now = this.actx.currentTime;
    if (now - this._lastBarkTime < 0.42 && !opts.force) return false; // no mush
    // A story line outranks ambient chatter, and the synth budget is ONE voice:
    // a boss taunt buried under his own men shouting CONTACT is the line the
    // player was meant to hear and did not.
    if (this.vox?.sounding(now) && !opts.force) {
      this.stats.barksYielded++;
      return false;
    }
    this._lastBarkTime = now;
    const seed = (opts.voice ?? 0) | 0;
    const o = {
      bark: barkFor(kind, this.rng),
      f0: 96 + ((seed * 37) % 41),
      tract: 0.95 + ((seed * 13) % 11) / 100,
      level: opts.level ?? 1,
      radio: opts.radio ?? false,
      send: opts.send,
    };
    if (position) return this._playAt('bark', position.x, position.y, position.z, o, 'voice', 0.85);
    return this._playDry('bark', o, 'voice', 0.25);
  }

  /**
   * Speak one authored story line — a chapter intro or outro beat, a brother's
   * advice, a boss taunt — in that speaker's own voice, with the radio and the
   * city bed ducked under it.
   *
   * @param {string} id speaker id: 'carson' | 'aidan' | 'dylan' | 'gabby' |
   *   'boss' | 'cop' | 'radio', or anything else (hashed to a stable voice).
   * @param {string} line the authored text. Its syllable count and stress
   *   pattern are what is performed — see `voiceLine` in vox.js for why it is
   *   deliberately not a pronunciation.
   * @param {number} dur seconds the line is on screen. 0 means "pace it
   *   naturally"; a real number makes the read fit inside it, by elision first
   *   and truncation last.
   */
  dialogue(id, line, dur = 0, opts = {}) {
    if (!this.running || !this.vox) return false;
    try {
      return this.vox.speak(id, line, dur, opts);
    } catch (err) {
      this._error(err);
      return false;
    }
  }

  /** Take the live line down. `reason` rides on `dialogue:end`. */
  stopDialogue(reason = 'stopped') {
    return this.vox?.stop(reason) ?? false;
  }

  /**
   * WHOSE LINE IS ON SCREEN RIGHT NOW? Polled, because nothing emits it — see
   * this file's header.
   *
   * Two performers, and they are not interchangeable. `ui.subs.cut` is the
   * chapter cutscene: the world is stopped, the letterbox is down, and the
   * lines are `{who, text}` records it holds for the whole scene. `ui.subs` is
   * the in-mission caption bar: ONE line over live gameplay, its text kept only
   * in the DOM, its identity recoverable from `t` resetting to zero. The cut
   * wins whenever both are up, which is also `Subtitles.update`'s own rule.
   */
  _pollDialogue() {
    const ui = this.ctx.peek?.('ui');
    const subs = ui?.subs;
    if (!subs) return;
    const cut = subs.cut;
    const cutUp = cut?.active === true;

    /*
     * A CUTSCENE IS NOT A PAUSE, even though `ui`'s arbiter freezes the clock
     * for one (`_wants.cut`). Gating on `isPaused()` alone would silence every
     * line in the game — the only ones it must silence are the lines a player
     * walked away from into the pause menu, the map or the phone.
     */
    const held = !cutUp && ui.isPaused?.() === true;
    if (held) {
      // Identity is deliberately NOT reset: on resume the poller sees the same
      // line it already spoke and stays quiet, which is what "no doubling"
      // means. The bed comes back up on the mixer's own release.
      if (!this._dlgHeld) { this.vox.stop('paused'); this._dlgHeld = true; }
      return;
    }
    this._dlgHeld = false;

    if (cutUp) {
      this._barkT = -1;
      const lines = cut.lines;
      const idx = cut.idx | 0;
      if (lines === this._cutLines && idx === this._cutIdx) return;
      this._cutLines = lines;
      this._cutIdx = idx;
      const l = Array.isArray(lines) ? lines[idx] : null;
      if (l && l.text) this.dialogue(l.who, l.text, 0, { source: 'cutscene' });
      else this.vox.stop('empty');
      return;
    }
    if (this._cutLines) {
      this._cutLines = null;
      this._cutIdx = -1;
      this.vox.stop('scene-end');
    }

    if (!subs.active) { this._barkT = -1; return; }
    // `t` counts up within a line and is reset to 0 by `_next()`, so a fall is
    // the only edge there is. `life` is the bar's OWN display duration — read,
    // not re-derived, so a change to its formula moves the read with it.
    const t = subs.t ?? 0;
    if (this._barkT < 0 || t < this._barkT) {
      const text = subs.line?.textContent ?? '';
      const who = subs.who?.textContent ?? '';
      if (text) this.dialogue(who, text, subs.life ?? 0, { source: 'subtitle' });
    }
    this._barkT = t;
  }

  setMasterVolume(v) { this.mixer?.setMasterVolume(v); }
  setBusVolume(bus, v) { this.mixer?.setBusVolume(bus, v); }
  setAmbienceIntensity(v) { if (this.ambience) this.ambience.intensity = clamp(v, 0, 3); }
  setOcclusionEnabled(v) { if (this.field) this.field.occlusionEnabled = !!v; }

  /* ================================================================ */
  /* events                                                           */
  /* ================================================================ */

  _wireEvents(ctx) {
    const ev = ctx.events;
    const on = (name, fn) => this._offs.push(ev.on(name, fn));

    on('weapon:fire', (p) => this._onFire(p));
    on('weapon:reload', (p) => this._onReload(p));
    on('weapon:shell', (p) => this._onShell(p));
    on('bullet:impact', (p) => this._onImpact(p));
    on('bullet:tracer', (p) => this._onTracer(p));
    on('explosion', (p) => this._onExplosion(p));
    on('player:footstep', (p) => this._onFootstep(p));
    on('player:land', (p) => this._onLand(p));
    on('player:state', (p) => this._onPlayerState(p));
    on('damage:dealt', (p) => this._onDamageDealt(p));
    on('damage:taken', (p) => this._onDamageTaken(p));
    on('actor:death', (p) => this._onDeath(p));
    // Optional: emitted by `ai` if it wants scripted chatter.
    on('ai:bark', (p) => this.bark(p?.kind ?? 'spot', p?.position, { voice: p?.voice ?? 0 }));

    /* ---- the open world ------------------------------------------ */
    on('vehicle:engine', (p) => { if (this.vehicles) this.vehicles.onEngine(p); });
    on('vehicle:skid', (p) => { if (this.vehicles) this.vehicles.onSkid(p); });
    on('vehicle:collision', (p) => { if (this.vehicles) this.vehicles.onCollision(p); });
    on('vehicle:destroyed', (p) => { if (this.vehicles) this.vehicles.onDestroyed(p); });
    on('vehicle:enter', (p) => this._onVehicleEnter(p));
    on('vehicle:exit', (p) => this._onVehicleExit(p));
    on('wanted:change', (p) => this._onWanted(p));
    on('weather:change', (p) => this._onWeather(p));
    on('time:hour', (p) => this._onHour(p));
    // `sky` currently announces itself with sky:changed; take either.
    on('sky:changed', (p) => this._onHour(p));

    /* ---- gameplay jingles ---------------------------------------- */
    on('ui:station', (p) => this._onStation(p));
    // economy:cash is the richer twin of the canonical money:change; while
    // only the canonical one exists we ring off it, and the moment the first
    // economy:cash arrives money:change is ignored so one transaction is
    // never two dings.
    on('economy:cash', (p) => this._onCash(p, true));
    on('money:change', (p) => this._onCash(p, false));
    on('pickup:collect', (p) => this._onPickup(p));
    on('game:service', (p) => this._onService(p));
    on('police:busted', () => this._sting('busted'));
    on('player:wasted', () => this._sting('wasted'));
    on('player:death', () => this._sting('wasted'));
    on('mission:start', () => {
      if (this.running) this._playDry('mission_start', {}, 'ui', 0.12);
    });
  }

  /** Is this actor the local player? Only they take the camera into the cabin. */
  _isPlayer(actor) {
    if (!actor) return true;                       // unlabelled = assume player
    if (actor === 'player' || actor.isPlayer === true) return true;
    const player = this.ctx.peek?.('player');
    return !!player && (actor === player || actor === player.actor || actor === player.character);
  }

  _onVehicleEnter(p) {
    if (!p?.vehicle) return;
    this.vehicles?.onEnter(p);
    if (!this._isPlayer(p.actor)) return;
    this.inVehicle = true;
    this._ownVehicle = p.vehicle;
    this._ownPos.has = false;
  }

  _onVehicleExit(p) {
    if (!p?.vehicle) return;
    this.vehicles?.onExit(p);
    if (!this._isPlayer(p.actor)) return;
    if (this._ownVehicle && p.vehicle !== this._ownVehicle) return;
    this.inVehicle = false;
    // Keep the handle: the radio is still playing in the car you just left.
  }

  _onWanted(p) {
    const level = clamp(p?.level ?? 0, 0, 5);
    const prev = p?.prev ?? this._wanted;
    this._wanted = level;
    this.police?.setWanted(level, prev);
    // The radio loses the argument with a helicopter.
    if (level >= 2) this.mixer?.duckBus('music', clamp(0.2 + level * 0.09, 0, 0.62), 1.5);
  }

  _onWeather(p) {
    if (!p) return;
    const w = this._weather;
    if (Number.isFinite(p.rain)) w.rain = clamp(p.rain, 0, 1);
    if (Number.isFinite(p.wetness)) w.wetness = clamp(p.wetness, 0, 1);
    if (Number.isFinite(p.wind)) w.wind = clamp(p.wind, 0, 1);
    if (p.state) w.state = p.state;
    this.ambience?.setWeather(w);
    this.vehicles?.setWeather(w.wetness);
  }

  _onHour(p) {
    const h = p?.hour;
    if (!Number.isFinite(h)) return;
    this._hour = h;
    this.ambience?.setHour(h);
  }

  _onFire(p) {
    if (!this.running || !p) return;
    const w = p.weapon;
    const name = typeof w === 'string' ? w : (w?.audio ?? w?.id ?? w?.name ?? w?.kind);
    let profile = resolveProfile(name);
    if (w && typeof w === 'object' && w.suppressed) profile = WEAPON_PROFILES.suppressed;

    if (p.empty) {
      this._playDry('dryfire', {}, 'weapons', 0.15);
      return;
    }

    const o = p.origin;
    const lp = this.field.listenerPos;
    const x = o?.x ?? lp.x, y = o?.y ?? lp.y, z = o?.z ?? lp.z;
    const dist = this.field.distanceTo(x, y, z);
    const firstPerson = p.firstPerson ?? dist < 2.6;

    if (firstPerson) {
      // Own weapon: no propagation delay, mostly dry, and the send level is
      // driven by the space probe so the *room* answers the shot — a tight slap
      // indoors, a long crack down the street.
      const echo = 0.35 + this._space.tight * 0.5 + this._space.street * 0.9 +
        this._space.tunnel * 1.0 + this._space.room * 0.75;
      this._playDry('shot', { profile, firstPerson: true }, 'weapons', echo * 0.6);
      this.mixer.duck(0.55, 0.1);
    } else {
      this._playAt('shot', x, y, z, { profile, firstPerson: false }, 'weapons', 0.95);
      this.mixer.duck(clamp(0.5 - dist * 0.004, 0.12, 0.5), 0.08);
      // Enemies opening fire get occasional chatter, so firefights feel alive
      // even before `ai` grows its own bark logic.
      const now = this.actx.currentTime;
      if (now - this._lastEnemyFire > 4.5 && this.rng.float() < 0.45) {
        this._lastEnemyFire = now;
        this.bark(this.rng.float() < 0.6 ? 'spot' : 'suppress', p.origin, { level: 0.9 });
      }
    }
  }

  _onReload(p) {
    if (!this.running) return;
    const w = p?.weapon;
    const name = typeof w === 'string' ? w : (w?.audio ?? w?.id ?? w?.name);
    const heavy = /lmg|shot|snip|m249|pkm/i.test(String(name ?? '')) ? 1.35 : 1;
    const phase = p?.phase ?? 'end';
    if (p?.position) {
      this._playAt('reload', p.position.x, p.position.y, p.position.z, { phase, heavy }, 'foley', 0.6);
    } else {
      this._playDry('reload', { phase, heavy }, 'foley', 0.22);
    }
  }

  _onShell(p) {
    if (!this.running || !p) return;
    if (this._budget.shell++ > 2) return;
    const pos = p.position;
    const lp = this.field.listenerPos;
    const x = pos?.x ?? lp.x, y = pos?.y ?? lp.y, z = pos?.z ?? lp.z;
    const dist = this.field.distanceTo(x, y, z);
    if (dist > 22) return;
    // Find what it will land on, so brass on sand does not ring like concrete.
    let surface = 'concrete';
    const phys = this.ctx.peek('physics');
    if (phys?.raycast) {
      const h = phys.raycast(x, y + 0.2, z, 0, -1, 0, 6, phys.MASK?.WORLD);
      if (h?.hit) surface = h.surface;
    }
    this._playAt('shell', x, y - 0.6, z, {
      surface,
      level: clamp(1 - dist * 0.02, 0.3, 1),
      flight: 0.25 + this.rng.range(0, 0.3),
    }, 'foley', 0.25);
  }

  _onImpact(p) {
    if (!this.running || !p) return;
    if (p.exit) return;                       // only the entry side gets a sound
    if (this._budget.impact++ > 4) return;
    const pt = p.point;
    if (!pt) return;
    const dist = this.field.distanceTo(pt.x, pt.y, pt.z);
    if (dist > 90) return;
    this._playAt('impact', pt.x, pt.y, pt.z, {
      surface: p.surface ?? 'concrete',
      energy: clamp((p.damage ?? 30) / 34, 0.35, 1.5),
    }, 'foley', 0.55);
    // The round cracking past you is a separate sound from the impact itself.
    if (dist < 6) {
      this._playAt('whizz', pt.x, pt.y, pt.z, { miss: dist, noDelay: true }, 'foley', 0.7);
    }
  }

  _onTracer(p) {
    if (!this.running || !p?.from || !p?.to) return;
    if (this._budget.whizz++ > 2) return;
    // Closest approach of the trajectory to the listener.
    const lp = this.field.listenerPos;
    const ax = p.from.x, ay = p.from.y, az = p.from.z;
    const dx = p.to.x - ax, dy = p.to.y - ay, dz = p.to.z - az;
    const len2 = dx * dx + dy * dy + dz * dz;
    if (len2 < 1e-6) return;
    const t = clamp(((lp.x - ax) * dx + (lp.y - ay) * dy + (lp.z - az) * dz) / len2, 0, 1);
    const cx = ax + dx * t, cy = ay + dy * t, cz = az + dz * t;
    const miss = Math.hypot(lp.x - cx, lp.y - cy, lp.z - cz);
    if (miss > 5) return;
    if (Math.hypot(lp.x - ax, lp.y - ay, lp.z - az) < 3) return; // our own muzzle
    const flight = (Math.sqrt(len2) * t) / (p.speed ?? 850);
    this._playAt('whizz', cx, cy, cz, { miss, noDelay: true, extraDelay: flight }, 'foley', 0.75);
  }

  _onExplosion(p) {
    if (!this.running || !p?.position) return;
    const pos = p.position;
    const dist = this.field.distanceTo(pos.x, pos.y, pos.z);
    this._playAt('explosion', pos.x, pos.y, pos.z, {
      radius: p.radius ?? 6, level: 1, send: 1.0,
    }, 'weapons', 1);
    this.mixer.duck(0.85, 0.35);
    // Concussion: total inside ~4 m, nothing past ~22 m.
    const near = clamp(1 - dist / 22, 0, 1);
    if (near > 0.1) this.mixer.concuss(Math.pow(near, 1.4));
  }

  _onFootstep(p) {
    if (!this.running) return;
    if (this._budget.step++ > 3) return;
    const pos = p?.position;
    const lp = this.field.listenerPos;
    const x = pos?.x ?? lp.x, y = pos?.y ?? lp.y - 1.6, z = pos?.z ?? lp.z;
    const dist = this.field.distanceTo(x, y, z);
    if (dist > 45) return;
    const gait = p?.gait ?? (p?.running ? 'run' : p?.crouched ? 'crouch' : 'walk');
    this._playAt('step', x, y, z, {
      surface: p?.surface ?? 'concrete', gait,
      level: p?.level ?? (dist < 2 ? 0.72 : 1),
    }, 'foley', 0.4);
  }

  _onLand(p) {
    if (!this.running) return;
    const lp = this.field.listenerPos;
    const v = Math.abs(typeof p?.velocity === 'number' ? p.velocity : (p?.velocity?.y ?? 4));
    this._playAt('step', lp.x, lp.y - 1.6, lp.z, {
      surface: p?.surface ?? 'concrete', gait: 'land',
      level: clamp(v / 7, 0.35, 1.7), gear: 1,
    }, 'foley', 0.7);
    if (v > 8.5) this._playDry('cloth', { level: 0.8 }, 'foley', 0.15);
  }

  _onPlayerState(p) {
    if (!this.running || !p) return;
    // `player` is the authority on whether we are in a car; vehicle:enter/exit
    // is the transition, this is the steady state, and they must not disagree.
    if (p.inVehicle !== undefined && !!p.inVehicle !== this.inVehicle) {
      this.inVehicle = !!p.inVehicle;
    }
    if (p.stance !== undefined && p.stance !== this._stance) {
      this._stance = p.stance;
      this._playDry('cloth', { level: 0.9 }, 'foley', 0.12);
    }
    if (p.ads !== undefined && p.ads !== this._ads) {
      this._ads = p.ads;
      this._playDry('cloth', { level: 0.45 }, 'foley', 0.1);
    }
    if (p.nitroOn !== undefined) {
      this._nitro.evOn = !!p.nitroOn;
      this._applyNitro(); // edge now, not next frame — the scream is a transient
    }
  }

  _onDamageDealt(p) {
    if (!this.running || !p) return;
    // "Damage dealt TO p.target" — `ai` also uses this for rounds that hit the
    // player, and a hitmarker tick for being shot at is backwards. Incoming
    // damage is handled by _onDamageTaken.
    const t = p.target;
    if (t === 'player' || t?.isPlayer === true || t === this.ctx.peek('player')) return;
    if (!this._isPlayerSource(p.source)) {
      // Somebody else's round. The man it landed on still grunts — that is HIS
      // voice, not the player's feedback — but the tick on the loudest bus
      // belongs to the shot the player actually took. Two brothers firing every
      // 1.5 s were ticking it roughly twice a second on their own.
      if (p.point && t && this.rng.float() < 0.3) this.bark('hurt', p.point, { level: 0.85 });
      return;
    }
    this.ui(p.headshot ? 'headshot' : 'hitmarker', 1);
    if (p.killed) this.ui('kill', 1);
    else if (p.point && p.target && this.rng.float() < 0.3) {
      this.bark('hurt', p.point, { level: 0.85 });
    }
  }

  /**
   * Did the PLAYER deal this? The same predicate `ui` applies before it draws a
   * hitmarker, and it has to be the same one or the X and the tick disagree.
   *
   * `source` is OPTIONAL on `damage:dealt`, so ABSENT means the player — the
   * one emitter that leaves it unset is `physics.emitImpact`, reachable only
   * from `physics.fireBullet`, whose only callers are the player's ballistics
   * and his own melee sweep. Everything else stamps itself: the crew, the
   * mission hostiles, a ped's punch, and the player's melee model.
   */
  _isPlayerSource(s) {
    if (s == null) return true;
    if (s === 'player') return true;
    const p = this.ctx.peek('player');
    return s === p || s.isPlayer === true || (!!p && s === p.movement);
  }

  _onDamageTaken(p) {
    if (!this.running || !p) return;
    if (typeof p.health === 'number') this._health = p.health;
    this.ui('damage', clamp((p.amount ?? 20) / 25, 0.4, 1.4));
    if ((p.amount ?? 0) > 12 && this.rng.float() < 0.5) {
      this._playDry('bark', { bark: 'hit', level: 0.5, f0: 108 }, 'voice', 0.1);
    }
  }

  _onDeath(p) {
    if (!this.running) return;
    const pt = p?.point;
    if (!pt) return;
    this.bark('death', pt, { level: 1, force: true, voice: (p?.actor?.id ?? 0) | 0 });
    this._playAt('bodyfall', pt.x, pt.y, pt.z, {
      level: 1, extraDelay: 0.45 + this.rng.range(0, 0.4),
    }, 'foley', 0.6);
  }

  /* ================================================================ */
  /* gameplay jingles                                                 */
  /* ================================================================ */

  /**
   * A one-per-moment card sting (busted / wasted). Rate-limited because the
   * same moment can arrive on two events (player:wasted and player:death) and
   * two overlapping four-note phrases are mud.
   */
  _sting(kind) {
    if (!this.running) return;
    const t = this.actx.currentTime;
    this._lastSting ??= { busted: -9, wasted: -9 };
    if (t - (this._lastSting[kind] ?? -9) < 2) return;
    this._lastSting[kind] = t;
    this._playDry(kind, {}, 'ui', 0.15);
    // The card owns the moment; the radio does not talk over it.
    this.mixer?.duckBus('music', 0.5, 2.2);
  }

  _onCash(p, fromEconomy) {
    if (!p || !Number.isFinite(p.amount) || p.amount === 0) return;
    if (fromEconomy) this._cashEventSeen = true;
    else if (this._cashEventSeen) return;
    if (!this.running) return;
    const t = this.actx.currentTime;
    if (t - this._lastCashT < 0.12) return; // both twins for one transaction
    this._lastCashT = t;
    // Bigger scores ring harder, in a narrow band: this fires constantly.
    const level = clamp(0.55 + Math.abs(p.amount) / 900, 0.55, 1.2);
    this._playDry('cash', { level, spend: p.amount < 0 }, 'ui', 0.05);
  }

  _onPickup(p) {
    if (!this.running) return;
    this._playDry(PICKUP_JINGLE[p?.kind] ?? 'pickup', { level: 1 }, 'ui', 0.06);
  }

  /**
   * `game:service` — refuel/repair/bodyshop rings. The event only marks the
   * edges ('start' payloads, then done), so the tick train between them is
   * scheduled here: a wrench stroke or a pump glug every few hundred ms until
   * the done chime. A safety timeout covers a producer that never says done
   * (interrupted service, save/load mid-ring).
   */
  _onService(p) {
    if (!p) return;
    const sv = this._service;
    if (p.done === true || p.phase === 'done') {
      if (sv.active) {
        sv.active = false;
        if (this.running) this._playDry('service_done', {}, 'ui', 0.08);
      }
      return;
    }
    const kind = p.kind === 'repair' || p.kind === 'bodyshop' ? 'repair' : 'fuel';
    if (!sv.active || sv.kind !== kind) {
      sv.active = true;
      sv.kind = kind;
      this._serviceTick(); // the first tick lands with the event itself
    }
    sv.until = (this.actx?.currentTime ?? 0) + 30;
  }

  _serviceTick() {
    if (!this.running) return;
    const sv = this._service;
    sv.timer = (sv.kind === 'repair' ? 0.34 : 0.52) + this.rng.range(0, 0.12);
    this._playDry(sv.kind === 'repair' ? 'service_repair' : 'service_fuel',
      { level: 0.75 + this.rng.range(0, 0.3) }, 'foley', 0.2);
  }

  _updateService(dt) {
    const sv = this._service;
    if (!sv.active) return;
    if (this.actx.currentTime > sv.until) { sv.active = false; return; }
    sv.timer -= dt;
    if (sv.timer <= 0) this._serviceTick();
  }

  /**
   * Nitro. The player carries the state (player:state.nitroOn, also readable
   * off player.vehicles.nitroOn — the event only fires when OTHER state
   * fields change, so both are watched and either can assert it). Rising
   * edge: the ignition scream (jingles.nitro) plus a sustained hiss/growl
   * loop that holds while the bottle is open. Degrades to nothing when the
   * player subsystem is absent or has no nitro yet.
   */
  _applyNitro() {
    const n = this._nitro;
    const veh = this.ctx.peek?.('player')?.vehicles;
    if (veh && typeof veh.nitroOn === 'boolean') n.pollOn = veh.nitroOn;
    const on = !!(n.evOn || n.pollOn);
    if (on === n.on || !this.running) return;
    n.on = on;
    const t = this.actx.currentTime;
    const loop = this._ensureNitroLoop();
    if (on) {
      this._playDry('nitro', { level: 1 }, 'vehicles', 0.15);
      loop?.g.gain.setTargetAtTime(0.3, t, 0.09);
    } else {
      loop?.g.gain.setTargetAtTime(0, t, 0.16);
    }
  }

  /** The held layer: bottle hiss through a bandpass over a low combustion
      growl, parked at zero gain and ramped by _applyNitro. */
  _ensureNitroLoop() {
    if (this._nitroLoop || !this.running) return this._nitroLoop;
    const actx = this.actx;
    const g = mkGain(actx, 0);
    const hiss = this.bank.source('white', this.rng, 1, true);
    const bp = biquad(actx, 'bandpass', 2500, 0.7);
    const hg = mkGain(actx, 0.8);
    series(hiss, bp, hg).connect(g);
    const growl = osc(actx, 'sawtooth', 138);
    const gl = biquad(actx, 'lowpass', 420, 1.1);
    const gg = mkGain(actx, 0.5);
    series(growl, gl, gg).connect(g);
    const drv = shaper(actx, saturationCurve(3, 0.4), '2x');
    g.connect(drv);
    drv.connect(this.mixer.bus('vehicles'));
    hiss.start(0, hiss._offset);
    growl.start(0);
    this._nitroLoop = { g, nodes: [hiss, bp, hg, growl, gl, gg, g, drv] };
    return this._nitroLoop;
  }

  /* ================================================================ */
  /* ambience callbacks                                               */
  /* ================================================================ */

  /** A burst of gunfire a long way off, with correct propagation delay. */
  _distantVolley() {
    if (!this.running) return;
    const rng = this.rng;
    const lp = this.field.listenerPos;
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(70, 240);
    const x = lp.x + Math.cos(a) * d;
    const z = lp.z + Math.sin(a) * d;
    const y = lp.y + rng.range(-2, 6);
    const profile = rng.pick([WEAPON_PROFILES.ak, WEAPON_PROFILES.rifle, WEAPON_PROFILES.lmg, WEAPON_PROFILES.sniper]);
    const rounds = 1 + ((rng.u32() % 6) | 0);
    const rate = rng.range(0.075, 0.13);
    for (let i = 0; i < rounds; i++) {
      this._playAt('shot', x, y, z, {
        profile, extraDelay: i * rate * rng.range(0.9, 1.1), maxDist: 400,
        gain: 4.5,
        occlusion: 0, // it is over the rooftops, not through them
      }, 'weapons', 0.2);
    }
  }

  _distantBoom() {
    if (!this.running) return;
    const rng = this.rng;
    const lp = this.field.listenerPos;
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(120, 330);
    this._playAt('explosion', lp.x + Math.cos(a) * d, lp.y + rng.range(0, 8), lp.z + Math.sin(a) * d, {
      radius: rng.range(6, 16), level: 1, maxDist: 400, occlusion: 0, gain: 6,
    }, 'weapons', 0.25);
  }

  _ambientOneShot() {
    if (!this.running) return;
    const rng = this.rng;
    const lp = this.field.listenerPos;
    const which = rng.pick(ONE_SHOTS);
    const far = which === 'heli' || which === 'siren';
    const d = far ? rng.range(90, 260) : rng.range(14, 90);
    const a = rng.range(0, Math.PI * 2);
    this._playAt('ambient',
      lp.x + Math.cos(a) * d,
      lp.y + rng.range(-1, which === 'heli' ? 28 : 5),
      lp.z + Math.sin(a) * d,
      {
        which, level: rng.range(0.55, 1), maxDist: 400,
        occlusion: far ? 0 : undefined,
        gain: far ? 14 : 2.5,
      }, 'ambience', 0.15);
  }

  _distantChatter() {
    if (!this.running) return;
    const rng = this.rng;
    const lp = this.field.listenerPos;
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(25, 75);
    this.bark(rng.pick(['advance', 'flank', 'copy', 'spot']), {
      x: lp.x + Math.cos(a) * d, y: lp.y, z: lp.z + Math.sin(a) * d,
    }, { level: 0.85, voice: rng.int(0, 9) });
  }

  /* ================================================================ */
  /* debug                                                            */
  /* ================================================================ */

  /**
   * Fire one of everything, through the real event bus. Used by
   * src/audio/probe.mjs to prove the live graph runs without throwing; also
   * handy from the browser console.
   */
  debugStorm() {
    if (!this.running) return { error: 'audio not running' };
    const lp = this.field.listenerPos;
    const at = (dx, dy, dz) => ({ x: lp.x + dx, y: lp.y + dy, z: lp.z + dz });
    const ev = this.ctx.events;
    ev.emit('weapon:fire', { weapon: 'rifle', origin: at(0.2, -0.1, -0.3), dir: { x: 0, y: 0, z: -1 }, seed: 1 });
    ev.emit('weapon:fire', { weapon: 'ak', origin: at(14, 0, -22), dir: { x: 0, y: 0, z: 1 }, seed: 2 });
    ev.emit('weapon:fire', { weapon: 'sniper', origin: at(-70, 3, 90), dir: { x: 1, y: 0, z: 0 }, seed: 3 });
    ev.emit('weapon:fire', { weapon: 'shotgun', origin: at(3, 0, -4), dir: { x: 0, y: 0, z: -1 }, seed: 4 });
    ev.emit('weapon:fire', { weapon: { id: 'mp5', suppressed: true }, origin: at(-2, 0, -3), dir: { x: 0, y: 0, z: -1 }, seed: 5 });
    ev.emit('weapon:fire', { weapon: 'rifle', origin: at(0.2, -0.1, -0.3), empty: true });
    const surfaces = ['concrete', 'metal', 'wood', 'dirt', 'sand', 'glass', 'water', 'foliage', 'fabric', 'flesh', 'rubber', 'plaster'];
    for (const s of surfaces) {
      ev.emit('bullet:impact', {
        point: at(this.rng.range(-6, 6), this.rng.range(0, 2), this.rng.range(-8, -2)),
        normal: { x: 0, y: 1, z: 0 }, incident: { x: 0, y: 0, z: -1 },
        surface: s, damage: 32, exit: false,
      });
      ev.emit('player:footstep', { position: at(0, -1.6, 0), surface: s, running: true });
    }
    ev.emit('weapon:shell', { position: at(0.3, -0.2, -0.2), velocity: { x: 1, y: 1, z: 0 } });
    for (const ph of ['start', 'magout', 'magin', 'end']) ev.emit('weapon:reload', { weapon: 'rifle', phase: ph });
    ev.emit('bullet:tracer', { from: at(-30, 0, -30), to: at(2, 0, 2), speed: 880 });
    ev.emit('player:land', { velocity: 9, surface: 'concrete' });
    ev.emit('player:state', { stance: 'crouch', sprinting: false, sliding: false, ads: true });
    ev.emit('damage:dealt', { target: { id: 3 }, amount: 34, headshot: true, killed: false, point: at(4, 0, -9) });
    ev.emit('damage:taken', { amount: 28, from: at(4, 0, -9), health: 24 });
    ev.emit('actor:death', { actor: { id: 3 }, point: at(4, -1.2, -9), impulse: { x: 0, y: 0, z: 0 } });
    for (const k of ['spot', 'reload', 'grenade', 'flank', 'suppress', 'advance', 'hurt', 'copy']) {
      this.bark(k, at(this.rng.range(-9, 9), 0, this.rng.range(-9, 9)), { force: true, voice: this.rng.int(0, 9) });
    }
    for (const w of ONE_SHOTS) {
      this._playAt('ambient', lp.x + 20, lp.y + 2, lp.z - 20, { which: w, level: 0.6 }, 'ambience', 0.1);
    }
    this._distantVolley();
    this._distantBoom();
    this._distantChatter();
    ev.emit('explosion', { position: at(6, 0, -7), radius: 8, damage: 120 });

    /* ---- the open world -------------------------------------------- */
    for (const layer of Object.keys(CITY_EVENTS)) this._cityEvent(layer);
    this._thunder();
    ev.emit('weather:change', { state: 'rain', wetness: 0.8, rain: 0.7, wind: 0.6 });
    ev.emit('time:hour', { hour: 21.5 });

    // One of every vehicle class, spread down the street, all at part throttle.
    const classes = ['sports', 'muscle', 'sedan', 'van', 'truck', 'police', 'bike', 'boat'];
    this._debugVehicles ??= classes.map((c, i) => ({ id: `dbg_${c}`, kind: c, index: i }));
    for (let i = 0; i < this._debugVehicles.length; i++) {
      const v = this._debugVehicles[i];
      const p = at((i - 3.5) * 9, -0.6, -14 - i * 4);
      v.position = p;
      ev.emit('vehicle:engine', {
        vehicle: v, class: v.kind, rpm: 900 + i * 620, throttle: 0.55,
        gear: 2 + (i % 3), load: 0.6, speed: 8 + i * 3, position: p,
      });
    }
    ev.emit('vehicle:skid', { vehicle: this._debugVehicles[1], wheel: 2, point: at(4, -0.7, -18), slip: 0.85, surface: 'asphalt' });
    ev.emit('vehicle:collision', { vehicle: this._debugVehicles[2], point: at(-6, -0.4, -12), impulse: 9000, speed: 8 });
    ev.emit('vehicle:enter', { vehicle: this._debugVehicles[0], actor: 'player', seat: 0 });
    ev.emit('wanted:change', { level: 3, prev: 0 });

    /* ---- gameplay jingles ------------------------------------------- */
    ev.emit('economy:cash', { amount: 350, total: 1350, reason: 'storm' });
    for (const kind of ['cash', 'health', 'armor', 'ammo', 'nitro', 'package']) {
      ev.emit('pickup:collect', { kind });
    }
    ev.emit('game:service', { kind: 'refuel', phase: 'start', progress: 0 });
    ev.emit('game:service', { kind: 'refuel', phase: 'done', progress: 1 });
    ev.emit('ui:station', { id: 'gold' });
    this.radio.tune('grease');
    // A story line last, so the storm also exercises the read, the side-chain
    // and the dialogue:start/:end pair through the real event bus.
    this.dialogue('carson', 'Every time it rains in this town I think of the bridge.', 0,
      { source: 'debugStorm' });
    return {
      ok: true, voices: this.field.stats.active, tracked: this.field.stats.tracked,
      errors: this.stats.errors,
    };
  }

  /** Drive one vehicle class through a full rev range. Used by the probe. */
  debugDrive(kind = 'muscle', seconds = 6) {
    if (!this.running) return { error: 'audio not running' };
    const ev = this.ctx.events;
    const lp = this.field.listenerPos;
    const veh = { id: `sweep_${kind}`, kind, position: { x: lp.x + 3, y: lp.y - 1, z: lp.z - 5 } };
    let t = 0;
    const step = () => {
      t += 1 / 60;
      const u = (t % seconds) / seconds;
      ev.emit('vehicle:engine', {
        vehicle: veh, class: kind,
        rpm: 800 + u * 6000, throttle: u < 0.85 ? 1 : 0, gear: 1 + Math.floor(u * 5),
        load: u < 0.85 ? 1 : -0.6, speed: u * 45, position: veh.position,
      });
      if (t < seconds) requestAnimationFrame(step);
    };
    step();
    return { ok: true, kind };
  }

  /** Snapshot for the dev overlay and the probe script. */
  report() {
    return {
      running: this.running,
      failed: this.failed,
      state: this.actx?.state ?? 'none',
      sampleRate: this.actx?.sampleRate ?? 0,
      voices: this.field?.stats.active ?? 0,
      tracked: this.field?.stats.tracked ?? 0,
      dropped: this.field?.stats.dropped ?? 0,
      stolen: this.field?.stats.stolen ?? 0,
      occlusionRays: this.field?.stats.occlusionRays ?? 0,
      space: this.stats.space,
      spaceWeights: this.mixer ? { ...this.mixer.spaceWeights } : null,
      enclosure: this._space.enclosure,
      meanFree: this._space.meanFree,
      deafness: this.deafness,
      limiterReduction: this.mixer?.reduction ?? 0,
      cabin: +this._cabin.toFixed(2),
      inVehicle: this.inVehicle,
      vehicles: this.vehicles ? { ...this.vehicles.stats } : null,
      radio: this.radio ? { ...this.radio.stats } : null,
      police: this.police ? { ...this.police.stats } : null,
      dialogue: {
        lines: this.stats.dialogue,
        speaking: this.vox?.sounding() ?? false,
        speaker: this.vox?.id ?? '',
        barksYielded: this.stats.barksYielded,
        musicDuck: +(this.mixer?.buses?.music?.duckAmount ?? 0).toFixed(3),
      },
      city: this.ambience?.report ?? null,
      updateMs: +this.stats.ms.toFixed(3),
      events: this.stats.events,
      errors: this.stats.errors,
    };
  }
}
